import { useCallback, useEffect, useRef, useState } from "react";
import { Flex, Form } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { addNewCard } from "../../../services/paymentMethods.js";
import { message } from "../../../utils/utils.js";
import FloatingLabelSelectItem from "../../../components/inputs/FloatingLabelSelectItem.js";
import FloatingLabelInput from "../../../components/inputs/FloatingLabelInput.js";
import ModalFooter from "../../../components/modal/ModalFooter.js";
import CustomForm from "../../../components/form";
import CustomCard from "../../../components/card";
import {
  getCardMetadataFromTokenResponse,
  loadCollectJs,
} from "../../../utils/collectJs.js";
import "../styles.css";

const PAYMENT_PROVIDER_NMI = "nmi";
const NMI_COLLECT_SUBMIT_ID = "nmi-collect-submit";

const AddNewCardForm = ({
  updateForm,
  onCancel = () => {},
  paymentConfig = { provider: "slimcd" },
}) => {
  const [buttonLoading, setButtonLoading] = useState(false);
  const [collectReady, setCollectReady] = useState(false);
  const [errors, setErrors] = useState([]);
  const [form] = Form.useForm();
  const pendingValuesRef = useRef(null);

  const deleteFormErrors = (fieldName) => {
    form.setFields([
      {
        name: fieldName,
        errors: [],
      },
    ]);
  };

  const onFinishFailed = (params) => {
    setErrors(params.errorFields);
    Object.keys(params.values).forEach((fieldName) => {
      deleteFormErrors(fieldName);
    });
  };

  const provider = paymentConfig.provider || "slimcd";
  const isNmi = provider === PAYMENT_PROVIDER_NMI;
  const nmiConfigMissing = isNmi && !paymentConfig.tokenizationKey;

  const submitCard = useCallback(
    (cardPayload) => {
      addNewCard(cardPayload)
        .then(() => updateForm())
        .then(() => {
          onCancel();
          message({
            message: "Your credit card has been successfully added",
            type: "success",
          });
        })
        .catch((error) => {
          const validationErrors = error.validationErrors || {};
          const errors = Object.keys(validationErrors).map((key) => {
            return {
              errors: validationErrors[key],
              name: [key],
            };
          });
          if (errors.length) {
            setErrors(errors);
            return;
          }
          message({ message: "Something went wrong", type: "error" });
          console.error("Add new card failed:", error);
        })
        .finally(() => setButtonLoading(false));
    },
    [onCancel, updateForm]
  );

  const submitTokenizedCard = useCallback(
    (tokenResponse) => {
      const cardPayload = {
        ...pendingValuesRef.current,
        ...getCardMetadataFromTokenResponse(tokenResponse),
      };

      if (!cardPayload.paymentToken) {
        setButtonLoading(false);
        message({ message: "Payment form is unavailable", type: "error" });
        return;
      }

      submitCard(cardPayload);
    },
    [submitCard]
  );

  useEffect(() => {
    let mounted = true;

    if (!isNmi) {
      setCollectReady(false);
      return () => {
        mounted = false;
      };
    }

    if (nmiConfigMissing) {
      setCollectReady(false);
      setErrors([
        {
          errors: ["Payment form is unavailable"],
          name: ["paymentToken"],
        },
      ]);
      return () => {
        mounted = false;
      };
    }

    loadCollectJs({
      tokenizationKey: paymentConfig.tokenizationKey,
      collectJsUrl: paymentConfig.collectJsUrl,
    })
      .then((collectJs) => {
        if (!mounted || !collectJs) {
          return;
        }

        collectJs.configure({
          paymentSelector: `#${NMI_COLLECT_SUBMIT_ID}`,
          variant: "inline",
          styleSniffer: false,
          customCss: {
            "background-color": "transparent",
            border: "0",
            "border-color": "transparent",
            "border-style": "none",
            "border-width": "0",
            "box-shadow": "none",
            color: "#1f1f1f",
            "font-family": "Arial, sans-serif",
            "font-size": "14px",
            height: "40px",
            "line-height": "40px",
            outline: "none",
            padding: "0 16px",
          },
          placeholderCss: {
            color: "#737373",
            "font-size": "14px",
          },
          focusCss: {
            outline: "none",
          },
          invalidCss: {
            color: "#1f1f1f",
          },
          price: "1.00",
          currency: "USD",
          country: "US",
          fields: {
            ccnumber: {
              selector: "#ccnumber",
              title: "Card Number",
              placeholder: "Card Number",
            },
            ccexp: {
              selector: "#ccexp",
              title: "Expiration Date",
              placeholder: "MM / YY",
            },
            cvv: {
              selector: "#cvv",
              title: "CVV",
              placeholder: "CVV",
            },
          },
          timeoutDuration: 10000,
          timeoutCallback: () => {
            setButtonLoading(false);
            setErrors([
              {
                errors: ["Card details are invalid or incomplete"],
                name: ["paymentToken"],
              },
            ]);
          },
          callback: submitTokenizedCard,
        });
        setCollectReady(true);
      })
      .catch((error) => {
        console.error("Collect.js failed to load:", error);
        setErrors([
          {
            errors: ["Payment form is unavailable"],
            name: ["paymentToken"],
          },
        ]);
      });

    return () => {
      mounted = false;
    };
  }, [
    isNmi,
    nmiConfigMissing,
    paymentConfig.collectJsUrl,
    paymentConfig.tokenizationKey,
    submitTokenizedCard,
  ]);

  const onFinish = async (values) => {
    setButtonLoading(true);

    if (!isNmi) {
      submitCard(values);
      return;
    }

    pendingValuesRef.current = values;

    if (!window.CollectJS || !collectReady || nmiConfigMissing) {
      setButtonLoading(false);
      message({ message: "Payment form is unavailable", type: "error" });
      return;
    }

    document.getElementById(NMI_COLLECT_SUBMIT_ID)?.click();
  };
  const renderCurrentError = (field) => {
    return errors.some((i) => i.name[0] === field);
  };

  const renderCardFields = () => {
    if (isNmi) {
      return (
        <>
          <div className="floating-wrapper">
            <div
              id="ccnumber"
              className={`collect-js-field ${
                renderCurrentError("paymentToken")
                  ? "collect-js-field-error"
                  : ""
              }`}
            />
          </div>
          <Flex gap={24} className="exp-date-wrapper collect-js-row">
            <div
              id="ccexp"
              className={`collect-js-field ${
                renderCurrentError("paymentToken")
                  ? "collect-js-field-error"
                  : ""
              }`}
            />
            <div
              id="cvv"
              className={`collect-js-field ${
                renderCurrentError("paymentToken")
                  ? "collect-js-field-error"
                  : ""
              }`}
            />
          </Flex>
        </>
      );
    }

    return (
      <>
        <FloatingLabelInput
          name="cardNumber"
          currentError={renderCurrentError("cardNumber")}
          maxLength={19}
        />
        <Flex gap={24} className="exp-date-wrapper">
          <FloatingLabelSelectItem
            name="expMonth"
            currentError={renderCurrentError("expMonth")}
          />
          <FloatingLabelSelectItem
            name="expYear"
            currentError={renderCurrentError("expYear")}
          />
          <FloatingLabelInput
            name="cvv"
            currentError={renderCurrentError("cvv")}
            maxLength={4}
          />
        </Flex>
      </>
    );
  };

  return (
    <div className="forms-container">
      <CustomForm
        form={form}
        onFinish={onFinish}
        setErrors={setErrors}
        onFailed={onFinishFailed}
      >
        <FloatingLabelInput
          name="cardholderName"
          currentError={renderCurrentError("cardholderName")}
        />
        {renderCardFields()}
        {isNmi && (
          <button
            id={NMI_COLLECT_SUBMIT_ID}
            type="button"
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: "hidden",
              clip: "rect(0, 0, 0, 0)",
              whiteSpace: "nowrap",
              border: 0,
            }}
            tabIndex={-1}
            aria-hidden="true"
          />
        )}
        {errors.some((i) => !!(i.errors || []).length) && (
          <CustomCard
            customClass="margin-top-md"
            description={
              <Flex align="flex-start">
                <div>
                  <ExclamationCircleFilled className="text-error" />
                </div>
                <ul className="errors-list">
                  <Form.ErrorList
                    className="text-error"
                    errors={errors.map((item) => (
                      <Flex gap={8}>
                        <li className="error-item text-black text-semibold">
                          {item.errors[0]}
                        </li>
                      </Flex>
                    ))}
                  />
                </ul>
              </Flex>
            }
          />
        )}
        <ModalFooter
          submitTitle="Add"
          loading={buttonLoading}
          disabled={buttonLoading || nmiConfigMissing || (isNmi && !collectReady)}
          htmlType="submit"
          onCancel={onCancel}
        />
      </CustomForm>
    </div>
  );
};

export default AddNewCardForm;
